import { CommonConstants } from './CommonConstants';
import { GetSupportedCountriesList } from './GetSupportedCountriesData';
import type { BillingCurrency } from '../types/BillingCurrency';

/** File-local constants for currency helpers. */
const localConstants = {
  // Minor currency units per major unit (cents per dollar). Both supported currencies (USD, CAD) are 2-decimal.
  minorUnitsPerMajor: 100,
  // Locale used to format currency amounts. en-US renders the locale-aware currency symbol (e.g. '$' for USD, 'CA$' for CAD).
  formatLocale: 'en-US',
  currencyStyle: 'currency' as const
} as const;

/**
 * Resolves the billing currency for a country code, falling back to the default (USD) when the country is unknown or has no mapped currency.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g. 'US', 'CA').
 * @returns The lowercase billing currency code (e.g. 'usd', 'cad').
 */
export function GetCurrencyForCountry(countryCode: string): BillingCurrency {
  const country = GetSupportedCountriesList().find((entry) => entry.code === countryCode);
  return (country?.currency as BillingCurrency | undefined) ?? CommonConstants.Database.BillingCurrency.USD;
}

/**
 * Returns the distinct set of billing currencies across all supported countries. The default currency (USD) is always first so callers can treat it as the canonical entry.
 *
 * @returns The distinct billing currencies (e.g. ['usd', 'cad']).
 */
export function GetSupportedCurrencies(): BillingCurrency[] {
  const defaultCurrency = CommonConstants.Database.BillingCurrency.USD;
  const distinct = new Set<BillingCurrency>([defaultCurrency]);
  for (const country of GetSupportedCountriesList()) distinct.add(country.currency as BillingCurrency);
  return Array.from(distinct);
}

/**
 * Resolves the amount to charge in a target currency. The per-currency map holds amounts for every non-default currency; the default currency uses the base amount. Any currency missing from the
 * map falls back to the default amount so a price is always available.
 *
 * @param defaultAmount - The amount in the plan's default currency, in minor units.
 * @param byCurrency - Per-currency amount overrides (keyed by lowercase currency code, excluding the default currency), in minor units.
 * @param defaultCurrency - The plan's default currency.
 * @param targetCurrency - The currency to resolve the amount for.
 * @returns The amount to charge in the target currency, in minor units.
 */
export function ResolveCurrencyAmount(defaultAmount: number, byCurrency: Record<string, number> | undefined, defaultCurrency: BillingCurrency, targetCurrency: BillingCurrency): number {
  if (targetCurrency === defaultCurrency) return defaultAmount;
  return byCurrency?.[targetCurrency] ?? defaultAmount;
}

/**
 * Resolves the currency a plan can actually settle in: the target currency when the plan carries a price for it (or it is the plan's default), otherwise the plan's default currency. This keeps
 * display and checkout consistent — a plan with no price for the target currency (e.g. a legacy USD-only plan) settles in its default currency instead of requesting an unsupported currency from
 * the billing provider, which would be rejected.
 *
 * @param byCurrency - Per-currency amount overrides (keyed by lowercase currency code, excluding the default currency).
 * @param defaultCurrency - The plan's default currency.
 * @param targetCurrency - The currency the caller would prefer to settle in.
 * @returns The currency the plan supports settling in.
 */
export function ResolveSupportedCurrency(byCurrency: Record<string, number> | undefined, defaultCurrency: BillingCurrency, targetCurrency: BillingCurrency): BillingCurrency {
  if (targetCurrency === defaultCurrency) return defaultCurrency;
  return byCurrency?.[targetCurrency] !== undefined ? targetCurrency : defaultCurrency;
}

/**
 * Formats a minor-unit amount as a localized currency string (e.g. 39700 + 'usd' renders '$397.00'; 39700 + 'cad' renders 'CA$397.00').
 *
 * @param amountMinor - The amount in minor currency units.
 * @param currency - The lowercase billing currency code.
 * @returns The formatted currency string.
 */
export function FormatCurrencyMinor(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(localConstants.formatLocale, { style: localConstants.currencyStyle, currency: currency.toUpperCase() }).format(amountMinor / localConstants.minorUnitsPerMajor);
}

/**
 * Returns the locale-aware currency symbol for a currency code (e.g. 'usd' renders '$', 'cad' renders 'CA$').
 *
 * @param currency - The lowercase billing currency code.
 * @returns The currency symbol.
 */
export function GetCurrencySymbol(currency: string): string {
  const parts = new Intl.NumberFormat(localConstants.formatLocale, { style: localConstants.currencyStyle, currency: currency.toUpperCase() }).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? currency.toUpperCase();
}
